import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe, StripeElements, StripeElementsOptions } from '@stripe/stripe-js';
import { Alert, Button } from '@marketplace/ui';
import { type BookingPayment } from '@marketplace/contracts';
import { tokenAppearance } from './appearance.js';
import { formatMoney, formatRateBps } from './money.js';
import { bookingPath } from './paths.js';
import { paymentErrorKey, type PaymentErrorKey } from './payment-errors.js';
import { stripeLocale, useStripeJs } from './stripe.js';

/**
 * `W5-T15` §3.1 — the call-out fee's card form: what is charged, the Payment Element, and Pay.
 *
 * It takes the `BookingPayment` exactly as `booking.create` answered it, from whoever called it: the
 * booking page's Pay now / Book again, and `W4-T17`'s award (E1 = A). The client secret stays in
 * this component's props and in Stripe.js: never in storage, a URL, history state or a log (AC 28).
 *
 * **It never says the payment succeeded.** A confirm that resolves, in any status, hands over to the
 * booking page, which shows what the booking says; only the webhook makes a booking `PAID`
 * (`W5-T02` AC 22). What it does show is why an attempt failed, because only Stripe.js knows that,
 * and Pay works again on the same intent: a second card is a second attempt, not a second charge.
 *
 * The title is an `h2`: the page around the checkout (the job, the booking) owns the `h1`.
 */
export interface CallOutCheckoutProps {
  payment: BookingPayment;
  locale: string;
}

export function CallOutCheckout({ payment, locale }: CallOutCheckoutProps): ReactElement {
  const { t } = useTranslation();
  const stripe = useStripeJs(payment.payment.publishableKey, locale);
  const { fee, providerShare, platformFee, platformShareBps, iva, total, ivaRateBps } =
    payment.payment;

  // `W5-T19` §4.4: the two shares are nested under the fee they split, and the IVA names its base —
  // the platform's fee only (`W5-T09` E14 = C). Every figure is the server's (AC 15).
  return (
    <section className="mp-section" data-testid="checkout">
      <h2>{t('checkout.title')}</h2>
      <dl data-testid="checkout-summary">
        <dt>{t('checkout.summary.fee')}</dt>
        <dd>
          {formatMoney(fee, locale)}
          <dl>
            <dt>{t('checkout.summary.providerShare')}</dt>
            <dd>{formatMoney(providerShare, locale)}</dd>
            <dt>
              {t('checkout.summary.platformFee', {
                share: formatRateBps(platformShareBps, locale),
              })}
            </dt>
            <dd>{formatMoney(platformFee, locale)}</dd>
          </dl>
        </dd>
        <dt>{t('checkout.summary.iva', { rate: formatRateBps(ivaRateBps, locale) })}</dt>
        <dd>{formatMoney(iva, locale)}</dd>
        <dt>{t('checkout.summary.total')}</dt>
        <dd>{formatMoney(total, locale)}</dd>
      </dl>
      <p>{t('checkout.summary.ivaNote')}</p>
      {stripe.isError ? (
        <>
          <Alert tone="error">{t('checkout.loadFailed')}</Alert>
          <Button onPress={() => void stripe.refetch()}>{t('checkout.retry')}</Button>
        </>
      ) : stripe.data === undefined ? null : (
        <CardForm stripe={stripe.data} payment={payment} locale={locale} />
      )}
    </section>
  );
}

function CardForm({
  stripe,
  payment,
  locale,
}: {
  stripe: Stripe;
  payment: BookingPayment;
  locale: string;
}): ReactElement {
  // Once per mount: `Elements` cannot take a new client secret or appearance afterwards.
  const [options] = useState<StripeElementsOptions>(() => ({
    clientSecret: payment.payment.clientSecret,
    locale: stripeLocale(locale),
    appearance: tokenAppearance(),
  }));

  return (
    <Elements stripe={stripe} options={options}>
      <PayForm payment={payment} locale={locale} />
    </Elements>
  );
}

type Outcome =
  | { kind: 'failed'; key: PaymentErrorKey }
  // Stripe.js resolved with an intent, whatever its status: the booking page watches the webhook.
  | { kind: 'confirmed' }
  // Already paid in another tab, or cancelled at expiry: the booking page says which.
  | { kind: 'notPayable' };

async function confirm(
  stripe: Stripe,
  elements: StripeElements,
  returnUrl: string,
): Promise<Outcome> {
  try {
    // Stripe's order: the Element validates its fields first, then the intent is confirmed.
    const submitted = await elements.submit();
    if (submitted.error !== undefined) {
      return { kind: 'failed', key: paymentErrorKey(submitted.error) };
    }
    // `if_required`: a card's 3DS runs in Stripe's dialog on this page, so the promise settles here.
    // `return_url` is where a redirect-based method would land, and it carries only the booking.
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });
    if (result.error === undefined) return { kind: 'confirmed' };
    if (result.error.code === 'payment_intent_unexpected_state') return { kind: 'notPayable' };
    return { kind: 'failed', key: paymentErrorKey(result.error) };
  } catch {
    return { kind: 'failed', key: 'booking.pay.failed' };
  }
}

function PayForm({ payment, locale }: CallOutCheckoutProps): ReactElement {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [confirming, setConfirming] = useState(false);
  // Cleared when Pay is pressed, so the same failure twice in a row is still a change to focus.
  const [failure, setFailure] = useState<PaymentErrorKey | null>(null);
  // State is a render behind a double press; the ref is not (AC 9).
  const inFlight = useRef(false);
  const alertSlot = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (failure === null) return;
    // `Alert` takes no ref, so its node is found in the slot. Focus moves to the failure, which is
    // where a keyboard or screen-reader user has to start from to try again (AC 10).
    const alert = alertSlot.current?.querySelector<HTMLElement>('[role="alert"]');
    if (alert === undefined || alert === null) return;
    alert.tabIndex = -1;
    alert.focus();
  }, [failure]);

  async function pay(): Promise<void> {
    if (inFlight.current || stripe === null || elements === null) return;
    inFlight.current = true;
    setConfirming(true);
    setFailure(null);

    const bookingId = payment.booking.id;
    const outcome = await confirm(
      stripe,
      elements,
      `${window.location.origin}${bookingPath(locale, bookingId, 'stripe')}`,
    );
    inFlight.current = false;

    if (outcome.kind === 'failed') {
      setConfirming(false);
      setFailure(outcome.key);
      return;
    }
    void navigate(
      outcome.kind === 'confirmed'
        ? bookingPath(locale, bookingId, 'checkout')
        : bookingPath(locale, bookingId),
    );
  }

  return (
    <form
      data-testid="checkout-form"
      // The Button's own pending state is `aria-disabled`; the form is what is busy.
      aria-busy={confirming}
      onSubmit={(event) => {
        event.preventDefault();
        void pay();
      }}
    >
      <PaymentElement />
      <div ref={alertSlot}>
        {failure === null ? null : <Alert tone="error">{t(failure)}</Alert>}
      </div>
      <Button
        variant="primary"
        type="submit"
        isPending={confirming}
        pendingLabel={t('checkout.paying')}
      >
        {t('checkout.pay', { total: formatMoney(payment.payment.total, locale) })}
      </Button>
    </form>
  );
}
